import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "@/api/axiosInstance";
import { type AdminCustomizationOption } from "./useAdmin";

// Customization Options CRUD hooks
export const useCustomizationOptions = (enabled: boolean = true) => {
    return useQuery<AdminCustomizationOption[]>({
        queryKey: ['admin', 'customizations'],
        queryFn: async () => {
            const { data } = await axiosInstance.get('/products/customizations/');
            return data.results || data;
        },
        enabled,
    });
};

export const useCustomizationOption = (id: number, enabled: boolean = true) => {
    return useQuery<AdminCustomizationOption>({
        queryKey: ['admin', 'customizations', id],
        queryFn: async () => {
            const { data } = await axiosInstance.get(`/products/customizations/${id}/`);
            return data;
        },
        enabled: enabled && !!id,
    });
};

export const useCreateCustomizationOption = () => {
    const queryClient = useQueryClient();

    return useMutation<AdminCustomizationOption, Error, Omit<AdminCustomizationOption, 'id' | 'created_at' | 'updated_at'>>({
        mutationFn: async (customizationData) => {
            const { data } = await axiosInstance.post('/products/customizations/', customizationData);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'customizations'] });
        },
    });
};

export const useUpdateCustomizationOption = () => {
    const queryClient = useQueryClient();

    return useMutation<AdminCustomizationOption, Error, { id: number; data: Partial<Omit<AdminCustomizationOption, 'id' | 'created_at' | 'updated_at'>> }>({
        mutationFn: async ({ id, data }) => {
            const { data: response } = await axiosInstance.patch(`/products/customizations/${id}/`, data);
            return response;
        },
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'customizations'] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'customizations', id] });
        },
    });
};

export const useDeleteCustomizationOption = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, number>({
        mutationFn: async (id: number) => {
            await axiosInstance.delete(`/products/customizations/${id}/`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'customizations'] });
        },
    });
};
